import { useEffect, useState } from "react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ExternalLink, AppWindow } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function AppTile({ app }) {
  const initials = (app.name || "?").slice(0, 2).toUpperCase();
  const handleOpen = (e) => {
    e?.stopPropagation?.();
    window.open(app.url, "_blank", "noopener,noreferrer");
  };
  return (
    <div
      data-testid={`app-tile-${app.id}`}
      className="app-tile group relative flex flex-col items-center text-center bg-card border border-border rounded-2xl p-6 sm:p-7"
    >
      <span className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <ExternalLink className="h-4 w-4 text-muted-foreground" />
      </span>

      <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-2xl bg-muted flex items-center justify-center overflow-hidden ring-1 ring-black/5">
        {app.logo ? (
          <img
            src={app.logo}
            alt={app.name}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="font-display font-bold text-3xl dp-gradient-text">
            {initials}
          </span>
        )}
      </div>

      <div className="mt-5 font-semibold text-base sm:text-lg text-foreground line-clamp-1">
        {app.name}
      </div>
      {app.description && (
        <div className="mt-1 text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
          {app.description}
        </div>
      )}

      <Button
        type="button"
        onClick={handleOpen}
        data-testid={`app-login-btn-${app.id}`}
        className="mt-4 w-full dp-gradient-bg text-white hover:opacity-90 transition-opacity font-medium"
      >
        Login
        <ExternalLink className="ml-1.5 h-4 w-4" />
      </Button>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/my-apps")
      .then(({ data }) => {
        if (mounted) setApps(data);
      })
      .catch((e) => {
        toast.error("Failed to load your apps");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const firstName = (user?.name || user?.email || "").split(" ")[0];

  return (
    <div className="min-h-screen dp-grain" data-testid="employee-dashboard">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 dp-fade-in">
        <div className="mb-8 sm:mb-12">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            Apps Portal
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Hi {firstName || "there"},
            <span className="block dp-gradient-text">your toolbox</span>
          </h1>
          <p className="mt-3 text-base text-muted-foreground max-w-xl">
            Quickly jump into the tools your team uses every day. Click a tile to
            open it in a new tab.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : apps.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-20 border border-dashed border-border rounded-2xl bg-card/50"
            data-testid="empty-apps-state"
          >
            <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
              <AppWindow className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-display font-semibold text-lg">
              No apps assigned yet
            </h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-sm">
              Ask your admin to grant you access to your team's tools. They'll
              show up here.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6"
            data-testid="apps-grid"
          >
            {apps.map((app) => (
              <AppTile key={app.id} app={app} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
